import { Component, OnDestroy, OnInit, AfterViewInit, ViewChild, ElementRef, Input, EventEmitter, Output } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { Router } from '@angular/router';
import { SocialService } from '../../../../../../services/social.service';
import { LoaderComponent } from '../../../../reusable/loader/loader.component';
import { ButtonComponent } from '@predigle/micro/component-ui';
import { ToastService } from '../../../../../../services/toast.service';
import { MatMenuModule } from '@angular/material/menu';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { ExitButtonComponent } from '../../../../../../shared/components/buttons/exit-button/exit-button.component';
import { TourAnchorDirective } from '../../../../../../features/learning-center/engine/tour-anchor.directive';

@Component({
  selector: 'app-dockmates',
  standalone: true,
  imports: [CommonModule, LoaderComponent, ButtonComponent, MatMenuModule, FormsModule, ExitButtonComponent, TourAnchorDirective],
  templateUrl: './dockmates.component.html',
  styleUrl: './dockmates.component.scss'
})
export class DockmatesComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() initialSubTab: string | null = null;
  @Input() initialMateId: string | null = null;
  @Output() tabChange = new EventEmitter<string>();
  @Output() openFullProfile = new EventEmitter<{ profileId: string; profileObject: any , activeTab?: string }>();
  @ViewChild('scrollSentinel') scrollSentinel!: ElementRef;
  private observer: IntersectionObserver | null = null;
  activeTab = 'find';
  mates: any[] = [];
  loading = false;
  page = 1;
  // A page has to be able to fill the grid on its own. At 10 the four-column
  // Find grid never overflowed the panel, so every screen needed a burst of
  // follow-up requests just to become scrollable; 24 covers six rows in one.
  pageSize = 24;
  hasMore = true;
  // Guards runaway auto-advancing through pages that map to zero usable mates.
  private autoPageFetches = 0;
  private readonly MAX_AUTO_PAGE_FETCHES = 20;
  /** Pending "is the sentinel still on screen?" check — see keepFillingViewport. */
  private sentinelRecheckRaf: number | null = null;
  /**
   * Consecutive pages pulled to fill the viewport without the user scrolling.
   * Reset by a real scroll (the observer firing) and by resetAndLoad. Caps the
   * burst so a list that never grows tall enough to push the sentinel off screen
   * can't keep requesting pages.
   */
  private viewportFills = 0;
  private readonly MAX_VIEWPORT_FILLS = 6;
  /** Bumped by every reset so an in-flight page can tell it has been superseded. */
  private loadToken = 0;
  incomingCount = 0;
  sentCount = 0;
  searchQuery = '';

  get displayMates(): any[] {
    if (this.activeTab === 'find' || !this.searchQuery.trim()) return this.mates;
    const q = this.searchQuery.toLowerCase().trim();
    return this.mates.filter(m => (m.name || '').toLowerCase().includes(q));
  }
  private searchSubject = new Subject<string>();
  private subscription: Subscription = new Subscription();

  // Profile Popup Properties
  showProfilePopup = false;
  selectedProfile: any = null;
  profileLoading = false;

  // Mutual-connections list popup (opened by tapping the "N Mutual Connections" row).
  showMutualPopup = false;
  mutualPopupMate: any = null;

  // Invite Message Popup Properties
  showInviteMessagePopup = false;
  inviteMessage = '';
  mateForInvite: any = null;

  constructor(
    private socialService: SocialService,
    private router: Router,
    private location: Location,
    public toast: ToastService,
  ) { }

  ngOnInit() {
    if (this.initialSubTab) {
      this.activeTab = this.initialSubTab;
    }

    this.loadMates();
    this.fetchIncomingCount();

    if (this.initialMateId) {
      this.openProfileById(this.initialMateId);
    }

    this.subscription.add(
      this.searchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(query => {
        this.searchQuery = query;
        // Find tab: server-side search (reload). Others: client-side filter via displayMates getter.
        if (this.activeTab === 'find') {
          this.resetAndLoad();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.observer?.disconnect();
    if (this.sentinelRecheckRaf !== null) cancelAnimationFrame(this.sentinelRecheckRaf);
  }

  ngAfterViewInit() {
    this.setupObserver();
  }

  private setupObserver() {
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && this.hasMore && !this.loading) {
        // A genuine scroll brought the sentinel back into view — the burst
        // budget is for filling an under-full screen, not for real paging.
        this.viewportFills = 0;
        this.loadMates();
      }
    }, {
      threshold: 0,
      rootMargin: '100px',
      root: null
    });

    if (this.scrollSentinel) {
      this.observer.observe(this.scrollSentinel.nativeElement);
    }
  }

  onSearchChange(event: any) {
    const value = event.target.value;
    this.searchSubject.next(value);
  }

  fetchIncomingCount() {
    this.socialService.getIncomingRequests(1, 1).subscribe({
      next: (res: any) => {
        this.incomingCount = res.count || 0;
      },
      error: (err: any) => {
        console.error('Error fetching incoming count:', err);
      }
    });
  }

  setActiveTab(tab: string) {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.location.replaceState(`/profile/dockmates/${tab}`);
    this.resetAndLoad();
    const contentArea = document.querySelector('.content-area') as HTMLElement;
    if (contentArea) contentArea.scrollTop = 0;
  }

  resetAndLoad() {
    this.mates = [];
    this.page = 1;
    this.hasMore = true;
    this.autoPageFetches = 0;
    this.viewportFills = 0;
    // Abandon any page still in flight: its response belongs to the previous
    // tab/query and is discarded on arrival. The `loading` flag has to be
    // released with it — otherwise the guard in loadMates() skips this load
    // entirely and the tab sits blank (no skeleton, no cards, no empty state)
    // until some later action happens to trigger another fetch.
    this.loadToken++;
    this.loading = false;
    // Drop a queued viewport re-check too — it belongs to the previous tab/query.
    if (this.sentinelRecheckRaf !== null) {
      cancelAnimationFrame(this.sentinelRecheckRaf);
      this.sentinelRecheckRaf = null;
    }
    this.loadMates();
  }

  loadMates() {
    if (this.loading || !this.hasMore) return;

    this.loading = true;
    const token = this.loadToken;
    let request;
    const tabAtRequestTime = this.activeTab;

    switch (tabAtRequestTime) {
      case 'find':
        request = this.socialService.findDockmates(this.page, this.pageSize, this.searchQuery);
        break;
      case 'my-dockmates':
        request = this.socialService.myDockmates(this.page, this.pageSize, this.searchQuery);
        break;
      case 'incoming':
        request = this.socialService.getIncomingRequests(this.page, this.pageSize, this.searchQuery);
        break;
      case 'sent':
        request = this.socialService.getSentRequests(this.page, this.pageSize, this.searchQuery);
        break;
      default:
        this.loading = false;
        return;
    }

    request.subscribe({
      next: (res: any) => {
        // Superseded by a newer load — that one owns `loading` now, so leave it.
        if (token !== this.loadToken) return;
        // Tab changed without going through resetAndLoad: drop the response but
        // release the flag, or the next load would be skipped.
        if (tabAtRequestTime !== this.activeTab) {
          this.loading = false;
          return;
        }

        let newMates = res.results || [];

        // Map incoming/sent requests or my-dockmates nested structure
        if (tabAtRequestTime === 'incoming' || tabAtRequestTime === 'sent' || tabAtRequestTime === 'my-dockmates') {
          if (tabAtRequestTime === 'incoming') this.incomingCount = res.count || 0;
          if (tabAtRequestTime === 'sent') this.sentCount = res.count || 0;

          newMates = newMates.map((item: any) => {
            if (!item) return null;

            let user;
            if (tabAtRequestTime === 'my-dockmates') {
              user = item.user;
            } else if (tabAtRequestTime === 'sent') {
              user = item.target || item.target_user;
            } else if (tabAtRequestTime === 'incoming') {
              user = item.requester || item.requestor;
            }

            if (!user) {
              console.warn(`No user object found in ${tabAtRequestTime} item:`, item);
              return null;
            }

            // Handle id vs _id consistently
            const userId = user._id || user.id;
            const requestId = item._id || item.id;
            const targetUserId = item.target_user_id || user.id;

            // Handle vessel name with multiple fallbacks
            let vesselName = 'No Vessel';
            if (item.vessel_name && typeof item.vessel_name === 'string') {
              vesselName = item.vessel_name;
            } else if (user.vessel_name && typeof user.vessel_name === 'string') {
              vesselName = user.vessel_name;
            } else if (user.vessel && typeof user.vessel === 'string') {
              vesselName = user.vessel;
            }

            const mutualCount = item.mutual_connection_count !== undefined ? item.mutual_connection_count : (user.mutual_connection_count || 0);
            const mutualImages = item.mutual_connection_images || user.mutual_connection_images || [];
            const mutualProfiles = item.mutual_connections_profiles || user.mutual_connections_profiles || item.mutual_connection_profiles || user.mutual_connection_profiles || [];

            return {
              ...user,
              _id: userId,
              id: userId,
              user_id: user.user_id || targetUserId,
              request_id: requestId,
              requested_at: item.requested_at,
              request_status: item.status,
              direction: item.direction,
              role: item.role,
              mutual_connections_count: mutualCount,
              mutual_connections_images: mutualImages,
              mutual_connections_profiles: mutualProfiles,
              vessel_name: vesselName,
              message: item.message
            };
          }).filter((m: any) => m !== null);
        } else if (tabAtRequestTime === 'find') {
          // Handle find tab results which are likely direct user objects or profiles
          newMates = newMates.map((item: any) => {
            let vesselName = 'No Vessel';
            if (item.vessel_name && typeof item.vessel_name === 'string') {
              vesselName = item.vessel_name;
            }
            return {
              ...item,
              vessel_name: vesselName
            };
          });
        }

        const receivedCount = (res.results || []).length;
        // De-duplicate against what's already loaded — the backend can return
        // overlapping items across pages, which otherwise shows the same dockmate
        // twice as you scroll.
        const existingIds = new Set(this.mates.map((m: any) => m._id ?? m.id));
        const uniqueNewMates = newMates.filter((m: any) => {
          const id = m._id ?? m.id;
          if (id == null) return true;
          if (existingIds.has(id)) return false;
          existingIds.add(id);
          return true;
        });
        this.mates = [...this.mates, ...uniqueNewMates];
        // Base pagination on the raw page size, not the mapped length. Mapping
        // can drop malformed items, which previously left mates.length < res.count
        // forever — stranding the user on a blank screen with a stuck loader
        // (the empty-state never rendered and the observer won't re-fire).
        this.hasMore = receivedCount >= this.pageSize;
        this.page++;
        this.loading = false;

        // Did the rendered list actually get longer? Measure AFTER de-duplication:
        // the discover endpoint can hand back a page we've already seen in full,
        // in which case the grid is unchanged no matter what `results` contained.
        const grew = uniqueNewMates.length > 0;

        // A page that added nothing visible — empty, unmappable, or all
        // duplicates — leaves the sentinel exactly where it was, and the
        // IntersectionObserver won't re-fire. Step forward on its behalf, but
        // strictly bounded: this is the only path that can advance without the
        // list growing, so an unbounded version would spin on the server forever.
        if (!grew && this.hasMore && this.autoPageFetches < this.MAX_AUTO_PAGE_FETCHES) {
          this.autoPageFetches++;
          this.loadMates();
          return;
        }

        if (grew) {
          this.autoPageFetches = 0;
          this.keepFillingViewport();
        }
      },
      error: (err: any) => {
        console.error('Error loading mates:', err);
        if (token !== this.loadToken) return;
        this.loading = false;
      }
    });
  }

  /**
   * Pull the next page while the sentinel is still on screen.
   *
   * An IntersectionObserver only reports *changes* in intersection. The sentinel
   * sits at the end of the grid, so on a short list it is already visible when
   * the observer is attached: it fires once, page 1 arrives, and — because the
   * sentinel never left the viewport — it never fires again. Find Captains
   * therefore stopped dead at one page of 10 whenever the grid didn't overflow
   * the panel, which is every wide screen (four cards a row).
   *
   * Re-checking geometry after each page settles closes that gap. It is
   * self-limiting: loading stops as soon as the grid grows tall enough to push
   * the sentinel out of view, or the server returns a short page (`hasMore`).
   */
  private keepFillingViewport(): void {
    if (this.sentinelRecheckRaf !== null) cancelAnimationFrame(this.sentinelRecheckRaf);
    this.sentinelRecheckRaf = requestAnimationFrame(() => {
      this.sentinelRecheckRaf = null;
      if (this.loading || !this.hasMore) return;
      if (this.viewportFills >= this.MAX_VIEWPORT_FILLS) return;
      const el = this.scrollSentinel?.nativeElement;
      if (!el || !el.isConnected) return;
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      // Same 100px look-ahead the observer's rootMargin uses.
      const onScreen = rect.top <= viewportH + 100 && rect.bottom >= -100;
      if (!onScreen) {
        this.viewportFills = 0; // screen is full; back to observer-driven paging
        return;
      }
      this.viewportFills++;
      this.loadMates();
    });
  }

  onScroll(event: any) {
    // Deprecated: replaced by IntersectionObserver
  }

  toggleRequest(mate: any) {
    if (mate.loading) return;

    if (mate.direction === 'outgoing' && !mate.is_friend) {
      this.cancelRequest(mate);
    } else if (!mate.direction && !mate.is_friend) {
      this.sendRequest(mate);
    }
  }

  sendRequest(mate: any) {
    if (mate.loading) return;
    this.mateForInvite = mate;
    this.inviteMessage = '';
    this.showInviteMessagePopup = true;
    document.body.style.overflow = 'hidden';
  }

  cancelInvitePopup() {
    this.showInviteMessagePopup = false;
    this.mateForInvite = null;
    this.inviteMessage = '';
    if (!this.showProfilePopup) {
      document.body.style.overflow = '';
    }
  }

  confirmSendRequest() {
    const mate = this.mateForInvite;
    if (!mate || mate.loading) return;

    mate.loading = true;
    const targetId = mate._id || mate.id;
    const message = this.inviteMessage.trim();

    this.showInviteMessagePopup = false;
    if (!this.showProfilePopup) {
      document.body.style.overflow = '';
    }

    this.socialService.sendRequest(targetId, message).subscribe({
      next: (res: any) => {
        mate.request_status = 'sent';
        mate.direction = 'outgoing';
        mate.request_id = res.data?._id || res.id || res._id;
        mate.loading = false;
        this.syncListItem(mate);
        this.showSuccess(res.message || 'Request sent successfully');
        if (this.showProfilePopup) this.closeProfile();
      },
      error: (err: any) => {
        console.error('Error sending request:', err);
        mate.loading = false;
        this.showError(err?.error?.message || 'Error sending request');
      }
    });
  }

  acceptRequest(mate: any) {
    const id = mate.request_id || (mate.pending_request?._id);
    if (!id || mate.loading) return;

    mate.loading = true;
    this.socialService.respondToRequest(id, 'accepted').subscribe({
      next: (res: any) => {
        mate.is_friend = true;
        mate.request_status = 'accepted'; // Update status to friend
        delete mate.direction; // No longer a pending request
        mate.loading = false;
        this.syncListItem(mate);
        if (this.activeTab === 'incoming') {
          this.mates = this.mates.filter(m => m._id !== mate._id);
          this.incomingCount--;
         
        }
        this.showSuccess(res.message || 'Request accepted successfully');
        if (this.showProfilePopup) this.closeProfile();
      },
      error: (err: any) => {
        console.error('Error accepting request:', err);
        mate.loading = false;
        this.showError(err?.error?.message || 'Error accepting request');
      }
    });
  }

  ignoreRequest(mate: any) {
    const id = mate.request_id || (mate.pending_request?._id);
    if (!id || mate.loading) return;

    mate.loading = true;
    this.socialService.respondToRequest(id, 'ignored').subscribe({
      next: (res: any) => {
        mate.request_status = 'ignored';
        mate.direction = null;
        // Optionally remove from list if in incoming tab
        if (this.activeTab === 'incoming') {
          this.mates = this.mates.filter(m => m._id !== mate._id);
          this.incomingCount--;
          this.sentCount--;
        }
        mate.loading = false;
        this.syncListItem(mate);
        this.showSuccess(res.message || 'Request ignored');
        if (this.showProfilePopup) this.closeProfile();
      },
      error: (err: any) => {
        console.error('Error ignoring request:', err);
        mate.loading = false;
        this.showError(err?.error?.message || 'Error ignoring request');
      }
    });
  }

  cancelRequest(mate: any) {
    const id = mate.request_id || (mate.pending_request?._id);
    if (!id || mate.loading) return;

    mate.loading = true;
    this.socialService.cancelRequest(id).subscribe({
      next: (res: any) => {
        // Remove from list if in sent tab
        if (this.activeTab === 'sent') {
          this.mates = this.mates.filter(m => m._id !== mate._id);
           this.sentCount--;
        } else {
          mate.direction = null;
          mate.request_status = 'cancelled';
        }
        mate.loading = false;
        this.syncListItem(mate);
        this.showSuccess(res.message || 'Request cancelled');
        if (this.showProfilePopup) this.closeProfile();
      },
      error: (err: any) => {
        console.error('Error cancelling request:', err);
        mate.loading = false;
        this.showError(err?.error?.message || 'Error cancelling request');
      }
    });
  }

  // ---------------- SNACKBAR HELPERS ----------------
  private showSuccess(message: string) {
    this.toast.success(message);
  }

  private showError(message: string) {
    this.toast.error(message);
  }

  // Helper to sync state between popup and main list
  syncListItem(updatedMate: any) {

    if (!updatedMate) return;
      if (this.activeTab === 'sent') {
          this.mates = this.mates.filter(m => m._id !== updatedMate._id);
        } 
    if (this.activeTab === 'incoming') {
          this.mates = this.mates.filter(m => m._id !== updatedMate._id);
          this.incomingCount--;
        }

    // Some endpoints use _id, others use id. We check both.
    const targetId = updatedMate._id || updatedMate.id;
    if (!targetId) return;

    const index = this.mates.findIndex(m => (m._id === targetId || m.id === targetId));
    if (index !== -1) {
      console.log(`Syncing list item for ${targetId} at index ${index}`);
      // Create a fresh object to trigger change detection
      this.mates[index] = { ...this.mates[index], ...updatedMate };
      this.mates = [...this.mates];
    } else {
      console.warn(`Could not find list item to sync for ID: ${targetId}`);
    }

    // Also sync the profile popup if it's open for this user
    if (this.showProfilePopup && this.selectedProfile) {
      const selectedId = this.selectedProfile._id || this.selectedProfile.id;
      if (selectedId === targetId) {
        this.selectedProfile = { ...this.selectedProfile, ...updatedMate };
      }
    }
  }

  getAvatar(mate: any) {
    return mate.image_url || 'assets/home/profile/dockmatesdummyicon.svg';
  }

  getSearchPlaceholder(): string {
    switch (this.activeTab) {
      case 'find': return 'Search for new Dockmates';
      case 'my-dockmates': return 'Search  Dockmates';
      case 'incoming': return 'Search Incoming Requests';
      case 'sent': return 'Search Sent Requests';
      default: return 'Search Dockmates';
    }
  }

  formatCount(count: number): string {
    return count < 10 ? `0${count}` : `${count}`;
  }

  getTimeAgo(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks}w ago`;
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }

  // --- Mutual connections popup ---
  openMutualConnections(mate: any, event: Event) {
    event.stopPropagation();
    if (!mate || (mate.mutual_connections_count || 0) === 0) return;
    this.mutualPopupMate = mate;
    this.showMutualPopup = true;
  }

  closeMutualConnections() {
    this.showMutualPopup = false;
    this.mutualPopupMate = null;
  }

  get mutualPopupProfiles(): Array<{ name: string; image_url: string }> {
    return (this.mutualPopupMate?.mutual_connections_profiles || [])
      .filter((p: any) => p && (p.name || p.image_url));
  }

  getMutualProfiles(mate: any): Array<{ name: string; image_url: string }> {
    const profiles = mate.mutual_connections_profiles;
    if (profiles && profiles.length > 0) {
      return profiles.slice(0, 3);
    }
    // Fallback: pad images array up to the actual count with empty slots
    const result: Array<{ name: string; image_url: string }> = (mate.mutual_connections_images || [])
      .slice(0, 3)
      .map((url: string) => ({ name: '', image_url: url }));
    const total = Math.min(mate.mutual_connections_count || 0, 3);
    while (result.length < total) {
      result.push({ name: '', image_url: '' });
    }
    return result;
  }

  onMutualImgError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.onerror = null;
    img.src = 'assets/home/profile/user_icon.svg';
    img.classList.add('mutual-img-error');
  }

  // ---------------- MENU ACTIONS ----------------
  selectedMate: any = null;
  selectedMateForMenu: any = null;
  showMenu: boolean = false;

  openMenu(mate: any) {
    this.selectedMateForMenu = mate;
  }

  closeMenu() {
    this.showMenu = false;
    this.selectedMate = null;
    document.body.style.overflow = '';
  }

  viewProfile() {
    const mate = this.selectedMateForMenu;
    if (mate) {
      this.openProfile(mate);
    }
  }

  removeConnection() {
    const mate = this.selectedMateForMenu;
    if (!mate) return;
    if (mate.removing) return; // ignore repeat clicks while in flight

    // Use the mate's user_id (GUID) as required by the API
    const userId = mate.user_id;
    const objectId = mate._id; // Keep for local list filtering

    // Keep the card visible with a loading overlay until the backend confirms —
    // then remove it and show the toast. (Better UX than a dead 3s wait.)
    mate.removing = true;
    this.socialService.removeConnection(userId).subscribe({
      next: () => {
        this.mates = this.mates.filter(m => m._id !== objectId);
        this.showSuccess('Connection removed');
      },
      error: (err: any) => {
        mate.removing = false;
        console.error('Error removing connection:', err);
        this.showError('Failed to remove connection');
      }
    });
  }

  // ---------------- PROFILE POPUP ----------------
  openProfile(mate: any) {
    const userId = mate._id || mate.id;
    if (!userId) return;

    this.location.replaceState(`/profile/dockmates/${this.activeTab}/${userId}`);
    this.showProfilePopup = true;
    this.profileLoading = true;
    this.selectedProfile = { ...mate }; // Show what we have

    this.socialService.getProfileById(userId).subscribe({
      next: (res: any) => {
        const processed = this.processProfile(res);
        this.selectedProfile = { ...this.selectedProfile, ...processed };
        // If it's from find tab, request_id might be missing but res might have pending_request
        if (res.pending_request && !this.selectedProfile.request_id) {
          this.selectedProfile.request_id = res.pending_request._id || res.pending_request.id;
          this.selectedProfile.direction = res.pending_request.direction;
        }
        this.profileLoading = false;
      },
      error: (err: any) => {
        console.error('Error fetching profile:', err);
        this.profileLoading = false;
      }
    });

    document.body.style.overflow = 'hidden';
  }

  dockmateActionFromProfile(mate: any) {
    const userId = mate._id || mate.id;
    if (!userId) return;
    this.profileLoading = true;
    this.selectedProfile = { ...mate }; // Show what we have
    this.viewFullProfile();
    // this.socialService.getProfileById(userId).subscribe({
    //   next: (res: any) => {
    //     const processed = this.processProfile(res);
    //     this.selectedProfile = { ...this.selectedProfile, ...processed };
    //     // If it's from find tab, request_id might be missing but res might have pending_request
    //     if (res.pending_request && !this.selectedProfile.request_id) {
    //       this.selectedProfile.request_id = res.pending_request._id || res.pending_request.id;
    //       this.selectedProfile.direction = res.pending_request.direction;
    //     }
    //     this.loadingService.loadingDockmate$.next(false);
        
    //   },
    //   error: (err: any) => {
    //     console.error('Error fetching profile:', err);
    //     this.loadingService.loadingDockmate$.next(false);
    //   }
    // });

    document.body.style.overflow = 'hidden';

  }

  openProfileById(userId: string) {
    this.showProfilePopup = true;
    this.profileLoading = true;
    this.selectedProfile = { _id: userId };

    this.socialService.getProfileById(userId).subscribe({
      next: (res: any) => {
        const processed = this.processProfile(res);
        this.selectedProfile = { ...processed };
        // We don't know the activeTab context perfectly if arriving directly, 
        // but UserProfile should have set it based on the URL already.
        this.profileLoading = false;
      },
      error: (err: any) => {
        console.error('Error fetching profile:', err);
        this.profileLoading = false;
        this.closeProfile();
      }
    });

    document.body.style.overflow = 'hidden';
  }

  closeProfile() {
    this.showProfilePopup = false;
    this.selectedProfile = null;
    this.location.replaceState(`/profile/dockmates/${this.activeTab}`);
    document.body.style.overflow = '';
  }

  viewTrips() {
    this.tabChange.emit('trips');
    this.closeProfile();
  }

  viewTripsAndWaypoints() {
    this.tabChange.emit('trips');
    this.closeProfile();
  }

  viewFullProfile() {
    const profileId = this.selectedProfile?._id || this.selectedProfile?.id;
    if (!profileId) return;
    const profileObject = { ...this.selectedProfile };
    this.closeProfile();
    this.openFullProfile.emit({ profileId, profileObject , 'activeTab': this.activeTab });
  }

  viewFullProfileForMate(mate: any): void {
    const profileId = mate._id || mate.id;
    if (!profileId) return;
    const isFriend = this.activeTab === 'my-dockmates' ? true : !!mate.is_friend;
    // Pass the tab too: the full profile needs to know the list this person came
    // from, because My Dockmates only ever lists confirmed friends.
    this.openFullProfile.emit({
      profileId,
      profileObject: { ...mate, is_friend: isFriend },
      activeTab: this.activeTab,
    });
  }

  private processProfile(profile: any): any {
    if (!profile) return null;

    // Map primary vessel (or fallback to first vessel) to vessel_details
    if (Array.isArray(profile.vessels) && profile.vessels.length > 0) {
      const primary = profile.vessels.find((v: any) => v.is_primary) || profile.vessels[0];
      if (primary) {
        profile.vessel_details = {
          ...primary,
          draft: primary.draft || primary.specifications?.draft,
          length_overall: primary.length_overall || primary.specifications?.length_overall
        };
      }
    }

    return profile;
  }
}
